import React, { useEffect, useState } from "react";
import cfg from "../config";

let _cachedVapidKey = null;
const getVapidPublicKey = async () => {
  if (_cachedVapidKey) return _cachedVapidKey;
  const res = await fetch("/api/vapid-public-key");
  if (!res.ok) throw new Error("VAPID-Key nicht verfügbar");
  const { publicKey } = await res.json();
  _cachedVapidKey = publicKey;
  return publicKey;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
};

const isIosDevice = () => {
  const ua = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const maxTouch = window.navigator.maxTouchPoints || 0;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (platform === "MacIntel" && maxTouch > 1) return true;
  return false;
};

const isStandaloneMode = () =>
  window.navigator.standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

const doSubscribe = async (reg) => {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "default";

  if (isIosDevice()) await new Promise((r) => setTimeout(r, 500));

  const vapidKey = await getVapidPublicKey();
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });

  const res = await fetch("/api/save-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + localStorage.getItem("authToken"),
    },
    body: JSON.stringify({ subscription }),
  });

  if (!res.ok) throw new Error("Server-Fehler beim Speichern");
  return "subscribed";
};

const S = {
  card: {
    background: '#1e2130',
    border: '1px solid #2e3347',
    borderRadius: 10,
    padding: '1rem 1.2rem',
    marginBottom: 16,
  },
  title: { color: '#e8eaf0', fontWeight: 600, fontSize: '0.97rem', marginBottom: 4 },
  muted: { color: '#7b82a0', fontSize: '0.82rem', marginBottom: 0 },
  hint: {
    background: '#13151f',
    border: '1px solid #2e3347',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#7b82a0',
    fontSize: '0.85rem',
    marginTop: 10,
  },
  success: {
    background: '#1a3a2a',
    border: '1px solid #2a5a3a',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#4ade80',
    fontSize: '0.88rem',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  denied: {
    background: '#3a1a1a',
    border: '1px solid #5a2a2a',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f87171',
    fontSize: '0.88rem',
  },
};

const PushNotifications = () => {
  // loading | prompting | subscribed | denied | default | ios-browser | ios-standalone
  const [status, setStatus] = useState('loading');
  const [busy, setBusy]     = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setStatus('unsupported');
        return;
      }

      const ios        = isIosDevice();
      const standalone = isStandaloneMode();

      if (ios && !standalone) {
        setStatus('ios-browser');
        return;
      }

      try {
        const reg      = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          // VAPID-Key prüfen — bei Mismatch alte Subscription verwerfen und neu subscriben
          const vapidKey = await getVapidPublicKey();
          const expectedKey = urlBase64ToUint8Array(vapidKey);
          const existingKey = existing.options?.applicationServerKey
            ? new Uint8Array(existing.options.applicationServerKey)
            : null;
          const keysMatch = existingKey &&
            existingKey.byteLength === expectedKey.byteLength &&
            existingKey.every((b, i) => b === expectedKey[i]);

          if (!keysMatch) {
            await existing.unsubscribe();
            // weiter zur frischen Subscription unten
          } else {
            await fetch("/api/save-subscription", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + localStorage.getItem("authToken"),
              },
              body: JSON.stringify({ subscription: existing }),
            }).catch(() => {});
            setStatus('subscribed');
            return;
          }
        }

        if (Notification.permission === 'denied') {
          setStatus('denied');
          return;
        }

        // default → sofort automatisch fragen (kein Klick nötig)
        setStatus('prompting');
        setBusy(true);
        const result = await doSubscribe(reg);
        setStatus(result);
      } catch {
        // Browser hat auto-prompt blockiert → Button anzeigen
        setStatus('default');
      } finally {
        setBusy(false);
      }
    };

    init();
  }, []);

  // Manueller Retry-Button (falls Browser den Auto-Prompt blockiert hat)
  const handleManualSubscribe = async () => {
    setBusy(true);
    try {
      const reg    = await navigator.serviceWorker.ready;
      const result = await doSubscribe(reg);
      setStatus(result);
    } catch (err) {
      console.error("Push-Fehler:", err);
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading' || status === 'unsupported') return null;

  if (status === 'subscribed') {
    return (
      <div style={S.success}>
        <span>🔔</span>
        <span>Push-Benachrichtigungen sind aktiviert</span>
      </div>
    );
  }

  if (status === 'ios-browser') {
    return (
      <div style={S.card}>
        <p style={S.title}>🔔 Push-Benachrichtigungen</p>
        <p style={{ ...S.muted, marginBottom: 10 }}>
          Damit du Benachrichtigungen empfangen kannst, installiere die App zuerst auf dem Home-Bildschirm.
        </p>
        <div style={S.hint}>
          <strong style={{ color: '#c8cfe0' }}>So geht's auf iPhone/iPad:</strong><br />
          Tippe auf das Teilen-Symbol <strong>⬆</strong> im Safari-Browser und wähle <em>„Zum Home-Bildschirm"</em>.<br />
          Öffne die App danach vom Home-Bildschirm aus.
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div style={S.card}>
        <p style={S.title}>🔔 Push-Benachrichtigungen</p>
        <div style={S.denied}>
          Benachrichtigungen wurden im Browser blockiert. Bitte erlaube sie in den Browser-Einstellungen und lade die Seite anschließend neu.
        </div>
      </div>
    );
  }

  if (status === 'prompting') {
    return (
      <div style={S.card}>
        <p style={S.title}>🔔 Push-Benachrichtigungen</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#7b82a0', fontSize: '0.88rem' }}>
          <span className="spinner-border spinner-border-sm" style={{ color: '#ffc107' }} />
          <span>Warte auf Berechtigung…</span>
        </div>
      </div>
    );
  }

  // 'default' oder 'ios-standalone' — Auto-Prompt wurde vom Browser blockiert, Button als Fallback
  return (
    <div style={S.card}>
      <p style={S.title}>🔔 Push-Benachrichtigungen</p>
      <p style={{ ...S.muted, marginBottom: 12 }}>
        Bleib auf dem Laufenden: Erhalte Benachrichtigungen zu neuen {cfg.eventTermPlural}, Tauschbörse-Angeboten und Nachrichten vom {cfg.adminRoleName}.
      </p>
      <button
        className="btn btn-warning btn-sm fw-semibold"
        onClick={handleManualSubscribe}
        disabled={busy}
        style={{ borderRadius: 8, padding: '7px 20px' }}
      >
        {busy
          ? <><span className="spinner-border spinner-border-sm me-2" />Wird aktiviert…</>
          : 'Benachrichtigungen aktivieren'
        }
      </button>
    </div>
  );
};

export default PushNotifications;
